import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Gift, Save } from "lucide-react";
import { useCampaign, HOLIDAYS, themeFor } from "@/context/CampaignContext";

const TEXT_KEY = {
  christmas: "christmas_banner_text",
  mothers_day: "mothers_day_banner_text",
  easter: "easter_banner_text",
  fathers_day: "fathers_day_banner_text",
  halloween: "halloween_banner_text",
};

export default function AdminCampaigns() {
  const { campaign, reload } = useCampaign();
  const [active, setActive] = useState("general");
  const [editing, setEditing] = useState("christmas"); // which banner text is showing in the editor
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setActive(campaign.active_holiday || "general");
    const next = campaign.active_holiday !== "general" ? campaign.active_holiday : "christmas";
    setEditing(next);
    setText(campaign[TEXT_KEY[next]] || "");
  }, [campaign]);

  const switchEditing = (h) => {
    setEditing(h);
    setText(campaign[TEXT_KEY[h]] || "");
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { active_holiday: active };
      if (editing in TEXT_KEY) payload[TEXT_KEY[editing]] = text;
      await api.put("/admin/campaign", payload);
      await reload();
      toast.success("Campaign saved");
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-campaigns-page" className="max-w-4xl space-y-8">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl text-[#F5F5DC] flex items-center gap-3">
          <Gift size={26} className="text-[#B87333]" />
          Holiday Campaigns
        </h1>
        <p className="text-[#A3A3A3] text-sm mt-2 leading-relaxed">
          Pick the holiday in season. Products tagged &quot;Perfect Gift&quot; will show a 🎁 badge across the shop,
          and the chosen banner appears at the very top of every public page.
        </p>
      </div>

      <section className="bg-[#111] border border-[#2A2A2A] p-6 sm:p-8">
        <p className="smallcaps mb-4">Active campaign</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="campaign-mode-grid">
          {HOLIDAYS.map((h) => {
            const isActive = active === h.value;
            return (
              <button
                key={h.value}
                type="button"
                onClick={() => setActive(h.value)}
                data-testid={`campaign-mode-${h.value}`}
                className={
                  "p-4 border text-sm uppercase tracking-widest transition-all " +
                  (isActive
                    ? "border-[#B87333] bg-[#B87333] text-[#0A0A0A] font-medium shadow-[0_0_0_2px_rgba(184,115,51,0.25)]"
                    : "border-[#2A2A2A] text-[#A3A3A3] hover:border-[#B87333]/60 hover:text-[#F5F5DC]")
                }
              >
                {h.label}
              </button>
            );
          })}
        </div>
      </section>

      <section className="bg-[#111] border border-[#2A2A2A] p-6 sm:p-8">
        <p className="smallcaps mb-2">Banner messages</p>
        <p className="text-[#A3A3A3] text-xs mb-4">Pick which holiday&apos;s banner text to edit. Saving updates whichever you&apos;ve just typed in.</p>

        <div className="flex flex-wrap gap-2 mb-4">
          {HOLIDAYS.filter((h) => h.value !== "general").map((h) => {
            const isOpen = editing === h.value;
            return (
              <button
                key={h.value}
                type="button"
                onClick={() => switchEditing(h.value)}
                data-testid={`campaign-edit-tab-${h.value}`}
                className={
                  "px-3 py-1.5 border text-[10px] uppercase tracking-widest " +
                  (isOpen
                    ? "border-[#B87333] text-[#B87333]"
                    : "border-[#2A2A2A] text-[#A3A3A3] hover:text-[#F5F5DC]")
                }
              >
                {h.label}
              </button>
            );
          })}
        </div>

        <label className="smallcaps block mb-2">{HOLIDAYS.find((h) => h.value === editing)?.label} banner text</label>
        <input
          className="input-dark"
          value={text}
          onChange={(e) => setText(e.target.value)}
          maxLength={240}
          data-testid="campaign-banner-text"
        />

        {/* Live preview */}
        {editing !== "general" && (
          <div className="mt-5">
            <p className="smallcaps mb-2 text-[#A3A3A3]">Preview</p>
            <div className={`${themeFor(editing)} px-6 py-3 text-center text-sm sm:text-base font-medium`} data-testid="campaign-banner-preview">
              {text || "—"}
            </div>
          </div>
        )}
      </section>

      <div className="flex justify-end">
        <button
          onClick={save}
          disabled={saving}
          className="btn-primary inline-flex items-center gap-2"
          data-testid="campaign-save-btn"
        >
          <Save size={14} />
          {saving ? "Saving…" : "Save campaign"}
        </button>
      </div>
    </div>
  );
}
